import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SensorsOutlined from '@mui/icons-material/SensorsOutlined';
import SyncAlt from '@mui/icons-material/SyncAlt';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { CompanyOnboardingStepKey } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { ReactNode, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { openIntercomMessage } from '~components/Intercom/intercom-utils';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import useCatenaConnect from '~hooks/useCatena/useCatenaConnect';
import useCreateRutterConnection from '~hooks/useRutter/useCreateRutterConnection';
import useRutterConnections from '~hooks/useRutter/useRutterConnections';
import StatusChip from '~pages/Settings/components/StatusChip';
import {
  catenaManageSettingsLink,
  rutterManageSettingsLink,
  settingsPagePath,
} from '~router/routePaths';
import { useStores } from '~store';

import {
  Eyebrow,
  OnboardingAccordion,
  OnboardingCard,
  SecondaryText,
  StepCallout,
  TitleText,
} from '../CompanyOnboardingPrimitives';

const COPY_PREFIX = 'company_onboarding.modules.integrations.work_area';

const trackConnectStarted = (integration: 'catena' | 'quickbooks') =>
  AnalyticsController.track('company_onboarding_integration_connect_started', {
    integration,
    step_key: CompanyOnboardingStepKey.INTEGRATIONS,
  });

interface IntegrationRowProps {
  action: ReactNode;
  body: string;
  icon: ReactNode;
  testId: string;
  title: string;
}

const IntegrationRow = ({ action, body, icon, testId, title }: IntegrationRowProps) => (
  <OnboardingCard
    data-test-id={`company-onboarding-integration-${testId}`}
    sx={{ alignItems: 'center', display: 'flex', gap: 2, mt: 1.5, p: 2 }}
  >
    <Box
      sx={(theme) => ({
        alignItems: 'center',
        backgroundColor: theme.semantic.surfaceSunken,
        borderRadius: 1,
        display: 'flex',
        flexShrink: 0,
        fontSize: 18,
        height: 44,
        justifyContent: 'center',
        width: 44,
      })}
    >
      {icon}
    </Box>
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <TitleText variant="h6">{title}</TitleText>
      <SecondaryText sx={{ mt: 0.5 }}>{body}</SecondaryText>
    </Box>
    <Box sx={{ alignItems: 'center', display: 'flex', flexShrink: 0, gap: 1 }}>
      {action}
    </Box>
  </OnboardingCard>
);

const ManageAction = ({
  isConnected,
  managePath,
}: {
  isConnected: boolean;
  managePath: string;
}) => (
  <>
    {isConnected ? (
      <StatusChip isSuccess label={t('administration.integration.connected')} />
    ) : null}
    <SmallButton
      color="secondary"
      component={RouterLink}
      to={managePath}
      variant="outlined"
    >
      {t('administration.integration.manage')}
    </SmallButton>
  </>
);

const QuickBooksAction = observer(() => {
  const { rutterIntegrationStore } = useStores();
  const { fetchConnections } = useRutterConnections();
  const [hasFetchFailed, setHasFetchFailed] = useState(false);

  const loadConnections = async () => {
    setHasFetchFailed(!(await fetchConnections()));
  };

  const { createRutterConnection } = useCreateRutterConnection({
    onLinked: loadConnections,
  });

  useEffect(() => {
    void loadConnections();
  }, []);

  const handleSetUp = async () => {
    if (await createRutterConnection()) {
      trackConnectStarted('quickbooks');
    }
  };

  if (hasFetchFailed) {
    return (
      <SmallLoadingButton
        color="secondary"
        data-test-id="company-onboarding-integration-quickbooks-retry"
        loading={rutterIntegrationStore.isLoadingConnections}
        onClick={loadConnections}
        variant="outlined"
      >
        {t('actions.retry')}
      </SmallLoadingButton>
    );
  }

  const connections = rutterIntegrationStore.connections ?? [];

  if (connections.length) {
    return (
      <ManageAction
        isConnected={connections.some((connection) => connection.isConnected)}
        managePath={settingsPagePath(rutterManageSettingsLink)}
      />
    );
  }

  return (
    <SmallLoadingButton
      color="secondary"
      data-test-id="company-onboarding-integration-quickbooks-set-up"
      disabled={rutterIntegrationStore.isLoadingConnections}
      loading={rutterIntegrationStore.isSavingConnection}
      onClick={handleSetUp}
      variant="outlined"
    >
      {t('company_onboarding.actions.set_up')}
    </SmallLoadingButton>
  );
});

const CatenaAction = () => {
  const { canConnect, connect, linkedFleets, pendingFleetId, stopPending } =
    useCatenaConnect();
  const [isConnecting, setIsConnecting] = useState(false);

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      if (await connect()) {
        trackConnectStarted('catena');
      }
    } finally {
      setIsConnecting(false);
    }
  };

  if (linkedFleets.some((fleet) => fleet.isConnected)) {
    return (
      <ManageAction isConnected managePath={settingsPagePath(catenaManageSettingsLink)} />
    );
  }

  if (pendingFleetId) {
    return (
      <>
        <SecondaryText>{t(`${COPY_PREFIX}.telematics_pending`)}</SecondaryText>
        <SmallButton
          color="secondary"
          data-test-id="company-onboarding-integration-telematics-cancel"
          onClick={stopPending}
          variant="outlined"
        >
          {t('actions.cancel')}
        </SmallButton>
      </>
    );
  }

  return (
    <SmallLoadingButton
      color="secondary"
      data-test-id="company-onboarding-integration-telematics-set-up"
      disabled={!canConnect}
      loading={isConnecting}
      onClick={handleConnect}
      variant="outlined"
    >
      {t('company_onboarding.actions.set_up')}
    </SmallLoadingButton>
  );
};

const GroupLabel = ({ children }: { children: string }) => (
  <Eyebrow sx={{ mb: 0.5, mt: 3, textTransform: 'uppercase' }}>{children}</Eyebrow>
);

const IntegrationsStep = observer(() => {
  const { userStore } = useStores();
  const { canManageAccountingIntegration, canManageTelematicsIntegration } =
    userStore.getPermissions();

  return (
    <Box data-test-id="company-onboarding-integrations">
      <StepCallout
        body={t(`${COPY_PREFIX}.callout_body`)}
        title={t(`${COPY_PREFIX}.callout_title`)}
      />
      {canManageAccountingIntegration ? (
        <>
          <GroupLabel>{t(`${COPY_PREFIX}.accounting_label`)}</GroupLabel>
          <IntegrationRow
            action={<QuickBooksAction />}
            body={t(`${COPY_PREFIX}.quickbooks_body`)}
            icon={<AccountBalanceOutlined fontSize="inherit" />}
            testId="quickbooks"
            title={t(`${COPY_PREFIX}.quickbooks_title`)}
          />
          <IntegrationRow
            action={
              <SmallButton
                color="secondary"
                data-test-id="company-onboarding-integration-other-accounting-contact"
                onClick={() =>
                  openIntercomMessage(t(`${COPY_PREFIX}.other_accounting_message`))
                }
                variant="outlined"
              >
                {t(`${COPY_PREFIX}.other_accounting_action`)}
              </SmallButton>
            }
            body={t(`${COPY_PREFIX}.other_accounting_body`)}
            icon={<SyncAlt fontSize="inherit" />}
            testId="other-accounting"
            title={t(`${COPY_PREFIX}.other_accounting_title`)}
          />
        </>
      ) : null}
      {canManageTelematicsIntegration ? (
        <>
          <GroupLabel>{t(`${COPY_PREFIX}.telematics_label`)}</GroupLabel>
          <IntegrationRow
            action={<CatenaAction />}
            body={t(`${COPY_PREFIX}.telematics_body`)}
            icon={<SensorsOutlined fontSize="inherit" />}
            testId="telematics"
            title={t(`${COPY_PREFIX}.telematics_title`)}
          />
        </>
      ) : null}
      {canManageAccountingIntegration ? (
        <OnboardingAccordion sx={{ my: 2 }}>
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 0 }}>
            <Typography sx={{ fontWeight: 500 }}>
              {t(`${COPY_PREFIX}.accounting_question`)}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, px: 0 }}>
            <SecondaryText>{t(`${COPY_PREFIX}.accounting_answer`)}</SecondaryText>
          </AccordionDetails>
        </OnboardingAccordion>
      ) : null}
    </Box>
  );
});

export default IntegrationsStep;
